import { useState } from "react";
import { ShieldQuestion } from "lucide-react";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { cn } from "@/shared/cn";
import type { IntelligenceApprovalRequest } from "@/features/intelligence/lib/approval-frame";
import { hasArguments, toolCopy } from "@/features/intelligence/lib/tool-copy";
import { fontSizeVar } from "@/shared/ui/type-scale";

export interface ApprovalPromptProps {
  request: IntelligenceApprovalRequest;
  pending: boolean;
  onDecide: (approved: boolean, reason?: string) => void;
}

export function ApprovalPrompt({ request, pending, onDecide }: ApprovalPromptProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [reasonOpen, setReasonOpen] = useState(false);
  const copy = toolCopy(request.toolName);

  return (
    <section
      aria-label={t("intelligence.approval.title")}
      className={cn(
        "flex flex-col gap-[12px] rounded-[var(--radius-lg)] border p-[16px]",
        "bg-[var(--color-paper)] shadow-[var(--shadow-soft)]",
      )}
      style={{ borderColor: "var(--color-brand-line)" }}
    >
      <div className="flex items-center gap-[8px]">
        <ShieldQuestion size={16} aria-hidden className="text-[var(--color-brand)]" />
        <h2
          className="text-[var(--color-text-strong)]"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: fontSizeVar("md"),
            letterSpacing: "-0.01em",
          }}
        >
          {t("intelligence.approval.title")}
        </h2>
      </div>

      <dl className="flex flex-col gap-[8px]">
        <div className="flex items-baseline gap-[8px]">
          <dt
            className="shrink-0 text-[var(--color-text-faint)]"
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 600,
              fontSize: fontSizeVar("xs"),
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {t("intelligence.approval.tool")}
          </dt>
          <dd className="flex min-w-0 flex-col">
            <span
              className="min-w-0 truncate text-[var(--color-text-strong)]"
              style={
                copy
                  ? {
                      fontFamily: "var(--font-display)",
                      fontSize: fontSizeVar("md"),
                      fontWeight: 500,
                    }
                  : { fontFamily: "var(--font-mono)", fontSize: fontSizeVar("sm") }
              }
            >
              {copy ? t(copy.labelKey) : (request.toolName ?? t("intelligence.tool.unnamed"))}
            </span>
            {copy && request.toolName && (
              <span
                className="min-w-0 truncate text-[var(--color-text-faint)]"
                style={{ fontFamily: "var(--font-mono)", fontSize: fontSizeVar("xs") }}
              >
                {request.toolName}
              </span>
            )}
          </dd>
        </div>
        {hasArguments(request.argsText) && (
          <div className="flex min-w-0 flex-col gap-[4px]">
            <dt
              className="text-[var(--color-text-faint)]"
              style={{
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                fontSize: fontSizeVar("xs"),
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {t("intelligence.approval.args")}
            </dt>
            <dd>
              <pre
                className="max-h-[160px] overflow-auto rounded-[var(--radius-md)] bg-[var(--color-light-04)] p-[8px] text-[var(--color-text)]"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: fontSizeVar("xs"),
                  lineHeight: 1.5,
                }}
              >
                {request.argsText}
              </pre>
            </dd>
          </div>
        )}
      </dl>

      {(copy || request.detail) && (
        <p
          className="text-[var(--color-text)]"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: fontSizeVar("md"),
            lineHeight: "160%",
            letterSpacing: "-0.01em",
          }}
        >
          {copy ? t(copy.detailKey) : request.detail}
        </p>
      )}

      {reasonOpen && (
        <label className="flex flex-col gap-[4px]">
          <span
            className="text-[var(--color-text-muted)]"
            style={{ fontFamily: "var(--font-display)", fontSize: fontSizeVar("xs") }}
          >
            {t("intelligence.approval.reasonLabel")}
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("intelligence.approval.reasonPlaceholder")}
            className={cn(
              "rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-light-05)]",
              "px-[12px] py-[8px] text-[var(--color-text-strong)]",
              "placeholder:text-[var(--color-text-faint)] focus:outline-none",
              "focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
            )}
            style={{
              fontFamily: "var(--font-display)",
              fontSize: fontSizeVar("md"),
              letterSpacing: "-0.01em",
            }}
          />
        </label>
      )}

      <div className="flex items-center gap-[8px]">
        <button
          type="button"
          disabled={pending}
          onClick={() => onDecide(true, reason)}
          className={cn(
            "flex h-[36px] items-center rounded-[var(--radius-pill)] px-[16px]",
            "bg-[var(--color-brand)] text-[var(--color-text-on-brand)]",
            "transition-opacity duration-[var(--duration-fast)] hover:opacity-90",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: fontSizeVar("md"),
            letterSpacing: "-0.01em",
          }}
        >
          {t("intelligence.approval.approve")}
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={() => {
            if (!reasonOpen) {
              setReasonOpen(true);
              return;
            }
            onDecide(false, reason);
          }}
          className={cn(
            "flex h-[36px] items-center rounded-[var(--radius-pill)] border px-[16px]",
            "border-[var(--color-border-strong)] bg-transparent text-[var(--color-text)]",
            "transition-colors duration-[var(--duration-fast)] hover:bg-[var(--color-hover-faint)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: fontSizeVar("md"),
            letterSpacing: "-0.01em",
          }}
        >
          {t("intelligence.approval.reject")}
        </button>
      </div>
    </section>
  );
}
